import { accessTokenStore } from './access-token.store';
import { refreshTokenStore } from './refresh-token.store';
import { authEpochStore } from './auth-epoch.store';
import { clearSessionFlag, markSessionStarted } from './session-restore.util';

export type OfficeProfile = Record<string, unknown>;

/**
 * The signed-in EMPLOYEE (a VendorUser), when the session is one.
 *
 * A legacy office login has no employee at all, which is why every reader has
 * to cope with `null` — see `getSessionUser`.
 */
export interface SessionUser {
  id: string;
  name?: string;
  email?: string;
  accessLevel?: 'HQ' | 'BRANCHES' | string;
}

/** One branch an employee may work from, as the auth API shapes it. */
export interface AssignedBranch {
  id: string;
  name_ar?: string;
  name_en?: string;
  city?: string;
}

/** In-memory office/branch profile (from login). Not used for auth — cookie + access token are. */
let officeProfile: OfficeProfile | null = null;

/**
 * The employee block and its branch assignments.
 *
 * `null` means UNKNOWN, `[]` means "known, and there are none" (an HQ employee
 * has no assignments). The distinction is what tells a reload whether it still
 * has to ask `GET /auth/office/me` — see `AuthService.hydrateEmployeeProfile`.
 */
let sessionUser: SessionUser | null = null;
let assignedBranches: AssignedBranch[] | null = null;

/** Selected leaf Branch id for vendor scoping (`all` / empty = all HQ branches). */
const BRANCH_STORAGE_KEY = 'vendorSelectedBranchId';
const SESSION_ROLE_KEY = 'vendorSessionRole';
/**
 * Mirrors of the two in-memory values above, for the PRE-BOOTSTRAP paint only.
 *
 * The sidebar renders before `/auth/refresh-token` has answered, so without
 * these a reload drew the switcher-less static scope block and then had to swap
 * it. Neither is a credential: the signed token stays the only authority on who
 * the session is, and a forged value buys an employee a dropdown whose every
 * entry the backend refuses (`BRANCH_NOT_ASSIGNED`).
 */
const SESSION_USER_KEY = 'vendorSessionUser';
const ASSIGNED_BRANCHES_KEY = 'vendorAssignedBranches';

export function getSessionRole(): 'office' | 'branch' | null {
  // The SIGNED TOKEN is the authoritative claim, so it is read FIRST.
  //
  // This used to consult the in-memory profile and then localStorage before the
  // JWT, which meant a stale `vendorSessionRole` outranked the actual session.
  // Three office responses carry no role at all (set-password, verify-sms,
  // complete-profile) and never overwrite the key — so an HQ whose first
  // sign-in on that browser is the set-password step, after a branch had signed
  // in there without using Logout, was classified as a branch.
  //
  // That was harmless while the role only hid nav entries. BR-59 turned those
  // routes into guarded ones, so a misclassified HQ now gets redirected away
  // from branch management with no explanation. Reading the token first fixes
  // the cause rather than the symptom.
  const token = getAccessToken();
  if (token) {
    const payload = decodeJwtPayload(token) as { role?: string } | null;
    if (payload?.role === 'office' || payload?.role === 'branch') {
      return payload.role;
    }
  }
  // No token yet (pre-bootstrap paint): fall back to what we last knew.
  try {
    const fromProfile = officeProfile?.['role'];
    if (fromProfile === 'office' || fromProfile === 'branch') {
      return fromProfile;
    }
    const stored = localStorage.getItem(SESSION_ROLE_KEY);
    if (stored === 'office' || stored === 'branch') return stored;
  } catch {
    /* ignore */
  }
  return null;
}

export function isBranchSession(): boolean {
  return getSessionRole() === 'branch';
}

export function getSelectedBranchId(): string | null {
  if (isBranchSession()) {
    const locked =
      (officeProfile?.['branchId'] as string) ||
      (officeProfile?.['_id'] as string) ||
      getBranchIdFromAccessToken();
    return locked?.trim() || null;
  }
  try {
    const v = localStorage.getItem(BRANCH_STORAGE_KEY);
    if (!v || v === 'all') return null;
    return v;
  } catch {
    return null;
  }
}

export function setSelectedBranchId(branchId: string | null): void {
  if (isBranchSession()) {
    return;
  }
  try {
    if (!branchId || branchId === 'all') {
      localStorage.removeItem(BRANCH_STORAGE_KEY);
    } else {
      localStorage.setItem(BRANCH_STORAGE_KEY, branchId);
    }
  } catch {
    /* ignore */
  }
}

function normalizeToken(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let token = raw.trim();
  if (!token) return null;
  if (
    (token.startsWith('"') && token.endsWith('"')) ||
    (token.startsWith("'") && token.endsWith("'"))
  ) {
    token = token.slice(1, -1).trim();
  }
  if (/^Bearer\s+/i.test(token)) {
    token = token.replace(/^Bearer\s+/i, '').trim();
  }
  return token || null;
}

/** Remove legacy token keys from older app versions. */
export function clearLegacyAuthKeys(): void {
  localStorage.removeItem('token');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userData');
}

export function getAccessToken(): string | null {
  return accessTokenStore.get();
}

export function setAccessToken(token: string | null): void {
  accessTokenStore.set(token);
}

export function getRefreshToken(): string | null {
  return refreshTokenStore.get();
}

export function setRefreshToken(token: string | null): void {
  refreshTokenStore.set(token);
}

export function getOfficeProfile(): OfficeProfile | null {
  return officeProfile;
}

export function setOfficeProfile(profile: OfficeProfile | null): void {
  officeProfile = profile;
}

/** Decode a JWT payload segment (base64URL-safe: JWT uses `-`/`_` and omits `=` padding). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** HQ Office id — for branch sessions this is parent `officeId`, not JWT sub. */
export function getOfficeId(): string | null {
  if (officeProfile) {
    if (officeProfile['role'] === 'branch' || officeProfile['officeId']) {
      const parent = officeProfile['officeId'];
      if (parent != null && String(parent).trim()) {
        return String(parent).trim();
      }
    }
    const id = officeProfile['_id'] ?? officeProfile['id'];
    if (id != null && String(id).trim() && officeProfile['role'] !== 'branch') {
      return String(id).trim();
    }
  }
  return getOfficeIdFromAccessToken();
}

export function getOfficeIdFromAccessToken(): string | null {
  const token = getAccessToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token) as { sub?: string; role?: string } | null;
  if (payload?.role === 'office' && payload.sub?.trim()) {
    return payload.sub.trim();
  }
  return null;
}

export function getBranchIdFromAccessToken(): string | null {
  const token = getAccessToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token) as { sub?: string; role?: string } | null;
  if (payload?.role === 'branch' && payload.sub?.trim()) {
    return payload.sub.trim();
  }
  return null;
}

/**
 * The `uid` claim — the VendorUser id — or `null` for a legacy office/branch
 * token, which has none. This is the ONLY reliable "is this an employee
 * session" test: the role is `office` or `branch` either way.
 */
export function getUidFromAccessToken(): string | null {
  const token = getAccessToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token) as { uid?: string } | null;
  const uid = payload?.uid;
  return typeof uid === 'string' && uid.trim() ? uid.trim() : null;
}

function readStoredJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeStoredJson(key: string, value: unknown): void {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    /* ignore */
  }
}

function normalizeSessionUser(raw: unknown): SessionUser | null {
  if (!raw || typeof raw !== 'object') return null;
  const body = raw as Record<string, unknown>;
  const id = String(body['id'] ?? body['_id'] ?? '').trim();
  if (!id) return null;
  return {
    id,
    name: typeof body['name'] === 'string' ? body['name'] : undefined,
    email: typeof body['email'] === 'string' ? body['email'] : undefined,
    accessLevel:
      typeof body['accessLevel'] === 'string' ? body['accessLevel'] : undefined,
  };
}

function normalizeAssignedBranches(raw: unknown): AssignedBranch[] | null {
  if (!Array.isArray(raw)) return null;
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const body = item as Record<string, unknown>;
      const id = String(body['id'] ?? body['_id'] ?? '').trim();
      if (!id) return null;
      return {
        id,
        name_ar: typeof body['name_ar'] === 'string' ? body['name_ar'] : undefined,
        name_en: typeof body['name_en'] === 'string' ? body['name_en'] : undefined,
        city: typeof body['city'] === 'string' ? body['city'] : undefined,
      } as AssignedBranch;
    })
    .filter((b): b is AssignedBranch => !!b);
}

/** The signed-in employee, or `null` for a legacy office / branch login. */
export function getSessionUser(): SessionUser | null {
  if (sessionUser) return sessionUser;
  // Pre-bootstrap paint: the access token has not been restored yet.
  return normalizeSessionUser(readStoredJson(SESSION_USER_KEY));
}

/** Every branch this employee may work from. `[]` for HQ and for legacy logins. */
export function getAssignedBranches(): AssignedBranch[] {
  if (assignedBranches) return assignedBranches;
  return normalizeAssignedBranches(readStoredJson(ASSIGNED_BRANCHES_KEY)) ?? [];
}

/**
 * Whether the assignments are KNOWN, as opposed to merely empty.
 *
 * `GET /auth/office/me` is asked exactly once per load, and only when this is
 * false for a token that carries a `uid`.
 */
export function hasKnownAssignedBranches(): boolean {
  return (
    assignedBranches !== null ||
    normalizeAssignedBranches(readStoredJson(ASSIGNED_BRANCHES_KEY)) !== null
  );
}

/**
 * Whether the sidebar should offer a branch switcher.
 *
 * Contract §6: never inferred from the role — an HQ employee's role is `office`
 * and a legacy branch login's is `branch`, and neither may switch. The only
 * test is "this session is scoped to one branch AND the employee is assigned
 * more than one".
 */
export function canSwitchBranch(): boolean {
  return isBranchSession() && getAssignedBranches().length > 1;
}

/**
 * Write the employee block from a login / select / switch / `me` response.
 *
 * Exported so `AuthService` can apply a `GET /auth/office/me` body, which
 * carries `user` + `assignedBranches` but no tokens.
 */
export function applySessionProfile(res: unknown): void {
  if (!res || typeof res !== 'object') return;
  const body = res as Record<string, unknown>;
  sessionUser = normalizeSessionUser(body['user']);
  assignedBranches = normalizeAssignedBranches(body['assignedBranches']) ?? [];
  writeStoredJson(SESSION_USER_KEY, sessionUser);
  writeStoredJson(ASSIGNED_BRANCHES_KEY, assignedBranches);
}

/** After login: keep access token in memory + optional in-memory profile. */
export function applyLoginResponse(res: unknown): void {
  if (!res || typeof res !== 'object') {
    return;
  }
  const body = res as Record<string, unknown>;
  const token = normalizeToken(
    (body['accessToken'] as string) || (body['token'] as string),
  );
  if (token) {
    accessTokenStore.set(token);
    // BLK-36: remember that a session exists, so the NEXT load knows it is worth
    // asking the refresh cookie. Without this the app cannot tell a returning
    // vendor from a first-time visitor and asks on every load.
    markSessionStarted();
  }

  const refresh = normalizeToken(body['refreshToken'] as string);
  if (refresh) {
    refreshTokenStore.set(refresh);
  }

  const role =
    (body['role'] as string) ||
    ((body['office'] as Record<string, unknown>)?.['role'] as string) ||
    null;

  const profile = (body['office'] ||
    body['branch'] ||
    body['user'] ||
    body) as OfficeProfile;
  if (profile && typeof profile === 'object') {
    officeProfile = { ...profile };
    delete officeProfile['accessToken'];
    delete officeProfile['token'];
    delete officeProfile['refreshToken'];
    if (role === 'branch' || role === 'office') {
      officeProfile['role'] = role;
    }
    if (body['branchId']) {
      officeProfile['branchId'] = body['branchId'];
    }
    if (body['officeId']) {
      officeProfile['officeId'] = body['officeId'];
    }
  }

  try {
    if (role === 'branch' || role === 'office') {
      localStorage.setItem(SESSION_ROLE_KEY, role);
    }
  } catch {
    /* ignore */
  }

  // The employee block rides the SESSION, so it is rewritten whenever a new
  // token arrives — including for a legacy office login, whose body carries no
  // `user` and must therefore CLEAR a previous employee's assignments rather
  // than inherit them. A bodiless re-apply (no token) leaves them alone.
  if (token) {
    applySessionProfile(body);
  }

  if (role === 'branch') {
    const branchId = String(
      body['branchId'] ||
        officeProfile?.['branchId'] ||
        officeProfile?.['_id'] ||
        '',
    ).trim();
    if (branchId) {
      try {
        localStorage.setItem(BRANCH_STORAGE_KEY, branchId);
      } catch {
        /* ignore */
      }
    }
  }

  clearLegacyAuthKeys();
}

/** @deprecated Use applyLoginResponse */
export function storeAuthProfile(res: unknown): void {
  applyLoginResponse(res);
}

/** True when an access token or in-memory office profile exists. */
export function hasStoredProfile(): boolean {
  return !!getAccessToken() || !!getOfficeProfile();
}

export function clearAuthSession(): void {
  accessTokenStore.clear();
  refreshTokenStore.clear();
  officeProfile = null;
  sessionUser = null;
  assignedBranches = null;
  // BLK-35: invalidate any refresh already in the air. Clearing the token is not
  // enough on its own — the in-flight response would simply write it back.
  authEpochStore.bump();
  // BLK-36: the next load must not try to restore a session that just ended.
  clearSessionFlag();
  try {
    localStorage.removeItem(SESSION_ROLE_KEY);
    localStorage.removeItem(BRANCH_STORAGE_KEY);
    localStorage.removeItem(SESSION_USER_KEY);
    localStorage.removeItem(ASSIGNED_BRANCHES_KEY);
  } catch {
    /* ignore */
  }
  clearLegacyAuthKeys();
}

export function isAccessTokenExpired(
  token: string | null,
  skewSeconds = 30,
): boolean {
  if (!token) return true;
  const payload = decodeJwtPayload(token) as { exp?: number } | null;
  if (!payload) return true;
  if (!payload.exp) return false;
  return Date.now() >= (payload.exp - skewSeconds) * 1000;
}

/** One-time cleanup of legacy localStorage auth keys. */
export function migrateAuthStorage(): void {
  clearLegacyAuthKeys();
}
